import { Badge, Box, Breakpoint, ButtonGroup, SxProps, Tooltip } from '@mui/material';
import { BaseTheme, Button } from 'design-system';
import { ActionView as Action, ButtonView } from 'interfaces/graphqlViews';
import { lazy, MouseEvent, ReactNode, Suspense, useState } from 'react';
import { getPrefixedRoute } from 'services/theming';
import DownloadPdfButton from 'components/DownloadPdfButton';
import useCompleteAction from 'hooks/useCompleteAction';
import useHandleCustomSchemeAndQuery, { ActionResponse } from 'hooks/useHandleCustomSchemeAndQuery';
import { isIOS } from 'utils/is-ios';
import { methodDownloadPdfPage } from 'utils/market-pdf';
import { CompleteType } from 'generated/graphql';
import ActionList from './ActionList';
import { btnIconGlyph } from './BtnIcon';

// Loaded on demand: they only open once an action asks for them, and TableDialog renders ActionButtons itself.
const TableDialog = lazy(() => import('./TableDialog'));
const BankDepositDetailsDialog = lazy(() => import('./ExpandableTable/BankDepositDetailsDialog'));

type MuiVariant = 'text' | 'outlined' | 'contained';
type MuiColor = 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';

export interface ActionButtonProps extends ButtonView, ActionResponse {
  size?: 'small' | 'medium' | 'large';
  sx?: SxProps<BaseTheme>;
  fullWidth?: boolean;
  /** Renders the icon alone; see the design-system Button. */
  asIcon?: boolean;
  collapseBelow?: Breakpoint;
  /** Replaces the server's icon, for a button whose icon follows local state. */
  startIcon?: ReactNode;
  loading?: boolean;
  /**
   * Replaces running the button's actions, for a caller that handles the click itself.
   * runAction runs them anyway, for a caller that only adds to what the click does.
   */
  onClick?: (event: MouseEvent, runAction: () => void) => void;
  /** Runs when the action completes with CompleteType.Close. */
  onClose?: () => void;
  /** The entity the button acts on, which keys how its action completed; see useCompleteAction. */
  eTag?: string | string[];
  children?: ReactNode;
}

/**
 * Renders a server-defined Button and runs its actions: spread the GraphQL button
 * into it, as in <ActionButton {...button} />. The ActionResponse handlers answer
 * what its action did.
 */
const ActionButton = ({
  size = 'small',
  sx,
  fullWidth,
  asIcon,
  collapseBelow,
  startIcon,
  loading = false,
  onClick,
  onClose,
  onMutate,
  onError,
  onExpandRow,
  onNotify,
  onHide,
  onStale,
  eTag,
  children,
  ...button
}: ActionButtonProps) => {
  const [isActionListOpen, setIsActionListOpen] = useState(false);
  const completion = useCompleteAction({
    onComplete: button.actions.length === 1 ? button.actions[0].onComplete : undefined,
    eTag,
    onHide,
    onStale,
  });
  const {
    handleSchemeOrQuery,
    isLoading,
    tableDialog,
    paymentDetails,
    fileDownloadLoadingOverlay: FileDownloadLoadingOverlay,
  } = useHandleCustomSchemeAndQuery({
    onMutate,
    // A failed action releases the button, and the row's other buttons, from how it completed.
    onError: () => {
      completion.clear();
      onError?.();
    },
    onExpandRow,
    onNotify,
  });

  const action = button.actions[0];
  if (!action && !onClick && !button.disabled) return null;

  // A lone action can be a group heading with no URL of its own, as the documents button sends when every document
  // falls under one payment type; it opens the picker like several actions do.
  const isSingleAction = button.actions.length === 1 && !action.group?.length;
  const label = button.value || action?.value;
  const color = button.color.toLowerCase() as MuiColor;
  const isDisabled = button.disabled || loading || isLoading || completion.isDisabled;
  // A plain link renders as an anchor so it can be opened in a new tab from the
  // browser; a click still navigates through the action handler, which keeps the return path.
  const href =
    !onClick && isSingleAction && action.url.length > 0 ? getPrefixedRoute(action.url) || undefined : undefined;

  const runAction = (event: MouseEvent) => {
    if (!action) return;
    if (!isSingleAction) {
      setIsActionListOpen(true);
      return;
    }
    handleSchemeOrQuery(
      action.url,
      action.query,
      { onClose: action.onComplete?.type === CompleteType.Close ? onClose : undefined },
      {},
      event,
    );
  };

  const handleClick = (event: MouseEvent) => {
    event.stopPropagation();
    if (href) event.preventDefault();
    if (button.disabled) return;
    if (onClick) onClick(event, () => runAction(event));
    else runAction(event);
    completion.complete();
  };

  // iOS can't save a file from a method: action, so the PDF is fetched and shared instead.
  if (!onClick && isIOS() && isSingleAction && action.url.includes('method:download')) {
    return (
      <DownloadPdfButton page={methodDownloadPdfPage(action.url)} buttonName={label} isSmall color={button.color} />
    );
  }

  let rendered = (
    <Button
      size={size}
      variant={button.variant.toLowerCase() as MuiVariant}
      color={color}
      startIcon={startIcon ?? btnIconGlyph(button.icon)}
      asIcon={asIcon}
      collapseBelow={collapseBelow}
      fullWidth={fullWidth}
      sx={sx}
      disabled={isDisabled}
      loading={loading || isLoading}
      href={href}
      onClick={handleClick}
    >
      {completion.nextLabel ?? children ?? label}
    </Button>
  );

  if (action?.description) {
    rendered = (
      <ButtonGroup
        variant="contained"
        color="inherit"
        sx={(theme) => ({ borderRadius: 0, borderColor: theme.palette.grey[300], boxShadow: 'none' })}
      >
        <Button
          variant="outlined"
          color={color}
          size={size}
          disabled={isDisabled}
          sx={(theme) => ({
            whiteSpace: 'nowrap',
            borderColor: 'inherit',
            backgroundColor: theme.palette.common.white,
          })}
          onClick={handleClick}
        >
          {action.description}
        </Button>
        {rendered}
      </ButtonGroup>
    );
  }

  if (button.badge) {
    rendered = (
      <Badge
        variant="dot"
        overlap="circular"
        sx={(theme) => ({ '& .MuiBadge-badge': { backgroundColor: theme.palette.warning.main } })}
      >
        {rendered}
      </Badge>
    );
  }

  if (button.disabled && button.disabledReason) {
    rendered = (
      <Tooltip title={button.disabledReason}>
        {/* A disabled button swallows pointer events, so the span keeps the tooltip working. */}
        <Box component="span">{rendered}</Box>
      </Tooltip>
    );
  }

  return (
    <>
      {rendered}
      {isActionListOpen && (
        <ActionList
          open
          button={button}
          onClose={(chosen?: Action) => {
            setIsActionListOpen(false);
            if (chosen) handleSchemeOrQuery(chosen.url, chosen.query);
          }}
        />
      )}
      <FileDownloadLoadingOverlay />
      <Suspense fallback={null}>
        {tableDialog.content && <TableDialog data={[tableDialog.content]} onClose={tableDialog.clear} />}
        {paymentDetails.open && (
          <BankDepositDetailsDialog shipmentId={paymentDetails.id} onClose={paymentDetails.onClose} />
        )}
      </Suspense>
    </>
  );
};

export default ActionButton;
